import type { AgentResponse, AgentSideEffect } from "@/types/createAgent";

export type AgentSideEffectSource = "freeform" | "action_payload" | "system";

export interface AgentSideEffectResolution {
  sideEffects: AgentSideEffect[];
  blockedSideEffects: AgentSideEffect[];
}

const FREEFORM_BLOCKED_SIDE_EFFECT_TYPES = new Set<AgentSideEffect["type"]>([
  "generate_image",
  "open_virtual_try_on",
  "create_ticket",
  "request_location",
]);

const LEGACY_GENERATION_ACTION_KEYS = [
  "shouldGenerate",
  "generateType",
  "modificationPrompt",
] as const;

function hasOwnActionKey(
  actions: AgentResponse["actions"] | undefined,
  key: keyof NonNullable<AgentResponse["actions"]>
): boolean {
  return Boolean(actions && Object.prototype.hasOwnProperty.call(actions, key));
}

function hasLegacyGenerationAction(
  actions: AgentResponse["actions"] | undefined
): boolean {
  return LEGACY_GENERATION_ACTION_KEYS.some(key =>
    hasOwnActionKey(actions, key)
  );
}

/** Blocked trace types that can be known even from an incomplete legacy flag. */
export function blockedSideEffectTypesFromLegacyActions(
  actions: AgentResponse["actions"] | undefined,
  source: AgentSideEffectSource
): Array<AgentSideEffect["type"]> {
  if (source !== "freeform" || !actions) return [];

  return [
    ...(hasLegacyGenerationAction(actions)
      ? (["generate_image"] as const)
      : []),
    ...(actions.shouldCreateTicket === true
      ? (["create_ticket"] as const)
      : []),
    ...(actions.shouldRequestLocation === true
      ? (["request_location"] as const)
      : []),
  ];
}

export function sideEffectsFromActions(
  actions: AgentResponse["actions"] | undefined
): AgentSideEffect[] {
  if (!actions) return [];

  const sideEffects: AgentSideEffect[] = [];

  if (actions.shouldGenerate === true && actions.generateType) {
    sideEffects.push({
      type: "generate_image",
      generateType: actions.generateType,
      ...(actions.modificationPrompt
        ? { modificationPrompt: actions.modificationPrompt }
        : {}),
    });
  }

  if (actions.shouldCreateTicket === true) {
    sideEffects.push({ type: "create_ticket" });
  }

  if (actions.shouldRequestLocation === true) {
    sideEffects.push({ type: "request_location" });
  }

  return sideEffects;
}

export function resolveAgentSideEffectsForSource(
  sideEffects: AgentSideEffect[],
  source: AgentSideEffectSource
): AgentSideEffectResolution {
  if (source !== "freeform") {
    return {
      sideEffects,
      blockedSideEffects: [],
    };
  }

  return sideEffects.reduce<AgentSideEffectResolution>(
    (resolution, sideEffect) => {
      if (FREEFORM_BLOCKED_SIDE_EFFECT_TYPES.has(sideEffect.type)) {
        resolution.blockedSideEffects.push(sideEffect);
      } else {
        resolution.sideEffects.push(sideEffect);
      }
      return resolution;
    },
    { sideEffects: [], blockedSideEffects: [] }
  );
}

export function getAgentSideEffects(
  response: Pick<AgentResponse, "actions" | "sideEffects">
): AgentSideEffect[] {
  return Array.isArray(response.sideEffects)
    ? response.sideEffects
    : sideEffectsFromActions(response.actions);
}

/**
 * Resolves the authoritative side-effect list while still auditing legacy
 * execution flags. An explicit `sideEffects: []` prevents legacy actions from
 * executing, but it must not make an unsafe freeform generation, ticket, or
 * location request disappear from the blocked trace or leave legacy flags
 * behind.
 */
export function resolveAgentSideEffectsForResponse(
  response: Pick<AgentResponse, "actions" | "sideEffects">,
  source: AgentSideEffectSource
): AgentSideEffectResolution {
  const resolution = resolveAgentSideEffectsForSource(
    getAgentSideEffects(response),
    source
  );
  if (source !== "freeform" || !Array.isArray(response.sideEffects)) {
    return resolution;
  }

  const legacyBlocked = resolveAgentSideEffectsForSource(
    sideEffectsFromActions(response.actions),
    source
  ).blockedSideEffects;
  const alreadyBlocked = new Set(
    resolution.blockedSideEffects.map(sideEffect => sideEffect.type)
  );

  return {
    sideEffects: resolution.sideEffects,
    blockedSideEffects: [
      ...resolution.blockedSideEffects,
      ...legacyBlocked.filter(sideEffect => {
        if (alreadyBlocked.has(sideEffect.type)) return false;
        alreadyBlocked.add(sideEffect.type);
        return true;
      }),
    ],
  };
}

export function withAgentSideEffects<T extends AgentResponse>(
  response: T,
  options: { source?: AgentSideEffectSource } = {}
): T {
  const source = options.source ?? "freeform";
  const explicitSideEffects = Array.isArray(response.sideEffects);
  const rawSideEffects = explicitSideEffects
    ? response.sideEffects || []
    : sideEffectsFromActions(response.actions);
  const { sideEffects, blockedSideEffects } =
    resolveAgentSideEffectsForResponse(response, source);

  // `sideEffects: []` is authoritative for the current client, but leaving
  // legacy execution flags behind is an unsafe and misleading contract for any
  // other consumer. A freeform message may stage a visible action; only a
  // trusted action payload may carry the matching execution flag.
  const blockedTypes = new Set(
    blockedSideEffects.map(sideEffect => sideEffect.type)
  );
  const shouldStripGenerate =
    source === "freeform" && hasLegacyGenerationAction(response.actions);
  const shouldStripTicket = blockedTypes.has("create_ticket");
  const shouldStripLocation = blockedTypes.has("request_location");
  const safeActions = response.actions
    ? Object.fromEntries(
        Object.entries(response.actions).filter(([key]) => {
          if (
            shouldStripGenerate &&
            ["shouldGenerate", "generateType", "modificationPrompt"].includes(
              key
            )
          ) {
            return false;
          }
          if (shouldStripTicket && key === "shouldCreateTicket") return false;
          if (shouldStripLocation && key === "shouldRequestLocation") {
            return false;
          }
          return true;
        })
      )
    : undefined;
  const responseWithSafeActions =
    safeActions &&
    Object.keys(safeActions).length !==
      Object.keys(response.actions ?? {}).length
      ? { ...response, actions: safeActions }
      : response;

  if (
    !explicitSideEffects &&
    rawSideEffects.length === 0 &&
    responseWithSafeActions === response
  ) {
    return response;
  }

  return sideEffects.length !== rawSideEffects.length || explicitSideEffects
    ? {
        ...responseWithSafeActions,
        sideEffects,
      }
    : sideEffects.length > 0
      ? {
          ...responseWithSafeActions,
          sideEffects,
        }
      : responseWithSafeActions;
}
